/**
 * Форматирование ровно в том виде, в каком его показывает SEON на скриншотах
 * (`reference/screens/`) — локаль `en-US`, потому что образец англоязычный и
 * сверять вёрстку придётся с ним.
 */

const DATE = new Intl.DateTimeFormat("en-US", {
  month: "numeric",
  day: "numeric",
  year: "2-digit",
})

const DATE_FULL = new Intl.DateTimeFormat("en-US", {
  month: "numeric",
  day: "numeric",
  year: "numeric",
})

const TIME = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
})

const INTEGER = new Intl.NumberFormat("en-US")

const MONEY = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Прочерк вместо пустого места: пустая ячейка читается как «съехала вёрстка». */
export const EMPTY = "—"

function parse(value: string | null | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** `9/13/26` — верхняя строка колонок Created / Last updated. */
export function formatDate(value: string | null | undefined) {
  const date = parse(value)
  return date ? DATE.format(date) : EMPTY
}

/** `9/14/2026` — колонка Last trigger date, там год полностью. */
export function formatDateFull(value: string | null | undefined) {
  const date = parse(value)
  return date ? DATE_FULL.format(date) : EMPTY
}

/** `2:44:02 PM` — нижняя строка колонок Created / Last updated. */
export function formatTime(value: string | null | undefined) {
  const date = parse(value)
  return date ? TIME.format(date) : ""
}

export function formatInt(value: number | null | undefined) {
  return typeof value === "number" ? INTEGER.format(value) : EMPTY
}

/**
 * Проценты приходят из Postgres как `numeric` — в JSON это **строка**
 * (`"64.23"`), а не число: иначе на длинных дробях поехала бы точность.
 * Поэтому приводим сами и не полагаемся на то, что придёт `number`.
 */
export function formatPercent(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === "") return EMPTY
  const numeric = typeof value === "number" ? value : Number(value)
  if (Number.isNaN(numeric)) return EMPTY
  // SEON печатает без нулевого хвоста: `0%`, `68.1%`, `64.23%`.
  return `${Number(numeric.toFixed(2))}%`
}

export function formatMoney(
  value: string | number | null | undefined,
  currency: string | null | undefined,
) {
  const numeric = typeof value === "number" ? value : Number(value ?? 0)
  const amount = Number.isNaN(numeric) ? 0 : numeric
  return `${MONEY.format(amount)} ${currency ?? ""}`.trim()
}

/**
 * Сумма колонки AMOUNT. От `formatMoney` отличается одним: суммы **нет** —
 * значит прочерк, а не `0.00 EUR`.
 *
 * Разница не косметическая. Ноль — это результат: «транзакция на нулевую
 * сумму». Пустое поле — это отсутствие данных, и рисовать на его месте
 * правдоподобную цифру запрещено прямо (§7 AMEND-10).
 */
export function formatAmount(
  value: string | number | null | undefined,
  currency: string | null | undefined,
) {
  if (value === null || value === undefined || value === "") return EMPTY
  const numeric = typeof value === "number" ? value : Number(value)
  return Number.isNaN(numeric) ? EMPTY : formatMoney(numeric, currency)
}

/**
 * Риск-скор колонки SCORE — два знака после запятой (§4 AMEND-10).
 *
 * В базе это `numeric`, в JSON — строка; `toFixed` округляет её к тому же виду,
 * в каком счёт показан в SEON. Пустое остаётся пустым: ноль здесь — «правила не
 * набрали ни балла», и подменять им отсутствие счёта нельзя.
 */
export function formatScore(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === "") return EMPTY
  const numeric = typeof value === "number" ? value : Number(value)
  return Number.isNaN(numeric) ? EMPTY : numeric.toFixed(2)
}

/**
 * Теги приходят одной строкой через запятую — так они лежат в выгрузке.
 * Пустые куски выбрасываются: хвостовая запятая не должна рисовать пустой чип.
 */
export function splitTags(value: string | null | undefined): string[] {
  if (!value) return []
  return value
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag !== "")
}

/**
 * Направление, в которое правило двигает риск-скор. Берётся из
 * `action_details` (`"+85"`, `"-0.5"`, `"+0"`) — это и есть та величина,
 * которую SEON показывает в колонке ACTION.
 *
 * Цвет — как в образце: снижающие скор (отрицательные) зелёные, остальные
 * красные, включая ровный `+0`. Своей трактовки нуля тут не вводим: аналитик
 * читает эту колонку глазами, натренированными на SEON, и «улучшение» цвета
 * поменяло бы смысл сигнала, а не оформление.
 */
export function scoreDirection(actionDetails: string | null | undefined) {
  if (!actionDetails) return "neutral" as const
  const numeric = Number(actionDetails)
  if (Number.isNaN(numeric)) return "neutral" as const
  return numeric < 0 ? ("decrease" as const) : ("increase" as const)
}

/** `INSTANT` / `68 ms` — в выгрузке это строка, не число. Показываем как есть. */
export function formatRuntime(value: string | null | undefined) {
  return value && value.trim() !== "" ? value : EMPTY
}

export function formatText(value: string | null | undefined) {
  return value && value.trim() !== "" ? value : EMPTY
}

/**
 * Размер файла — так, как его показывает проводник, из которого файл и взяли.
 *
 * Делитель 1024, а подписи `KB/MB/GB`: строго это `KiB/MiB/GiB`, но владелица
 * сверяет число с тем, что видела у себя в папке, а там Windows и macOS печатают
 * ровно эту пару — делят на 1024 и подписывают `MB`. Своя, более честная
 * единица разошлась бы с её экраном на 7 % и читалась бы как «залилось не то».
 */
const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const

export function formatBytes(value: number | null | undefined) {
  if (typeof value !== "number" || Number.isNaN(value)) return EMPTY
  let size = value
  let unit = 0
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024
    unit += 1
  }
  // Байты целыми: «1.0 B» читается как сбой округления, а не как размер.
  const digits = unit === 0 ? 0 : size >= 100 ? 0 : 1
  return `${size.toFixed(digits)} ${BYTE_UNITS[unit]}`
}
